import React, { Suspense, useCallback, useState } from 'react';

import { jsonParse } from '@lyearn/core/utils/json';
import { Button } from '@lyearn/ui';
import Link from '@/components/Link/Link';
import useNavigation from '@/hooks/useNavigation';
import Setup1on1Modal from '@/modules/1on1s/components/Setup1on1Modal';
import AssignContentModal from '@/modules/corporate/modules/assignment/views/AssignmentManager/components/AssignContentModal';
import CreateFeedbackModal from '@/modules/corporate/modules/feedback/views/FeedbackWall/components/CreateFeedbackModal';
import RequestFeedbackModal from '@/modules/corporate/modules/feedback/views/FeedbackWall/components/RequestFeedbackModal';
import HabitCreateModal from '@/modules/habits/components/HabitModals/components/HabitCreateModal';
import CoachRightDrawer from '@/modules/knowledge-base-copilot/CoachRightDrawer';
import GivePraiseModal from '@/modules/praises/views/PraiseWall/components/GivePraiseModal';
import AddOrEditTaskDialogContainer from '@/modules/tasks/components/AddOrEditTaskDialog';
import SetupMeetingModal from '@/modules/team-meetings/components/SetupMeetingModal';
import {
  MeetingTypeEnum,
  RecommendedActionCtaTypeEnum,
  RecommendedActionTypeEnum,
} from '@/types/schema';

import { getActionPath, InsightActionModalState, modalPayloadSchemaMap } from '../schemas';
import { InsightsRecommendationItem } from '../types';

interface Props {
  recommendation: InsightsRecommendationItem;
}

function InsightRowAction(props: Props) {
  const { recommendation } = props;
  const { card, targetModule, type } = recommendation;
  const action = card?.actions?.[0];
  const navigation = useNavigation();
  const [modalUiState, setModalUiState] = useState<InsightActionModalState | null>(null);

  const openModal = useCallback(() => {
    setModalUiState({
      type: type as any, // NOTE: need to use any, since TS is unable to infer the mapped type as the required discriminated union
      payload: modalPayloadSchemaMap[type]?.safeParse(jsonParse(action?.prefill || '{}').data).data,
    });
  }, [action?.prefill, type]);

  const closeModal = useCallback(() => {
    setModalUiState(null);
  }, []);

  // const Icon = getRecommendationIcon(recommendation);

  if (!action) {
    return null;
  }

  if (action.type === RecommendedActionCtaTypeEnum.Url && action.url) {
    return (
      <Link href={action.url} target="_blank">
        <Button
          // EndIcon={ArrowForward}
          appearance="secondary"
          className="bg-white hover:bg-white"
          shape="square"
          size="small"
          variant="outlined">
          {action.label}
        </Button>
      </Link>
    );
  }

  if (action.type === RecommendedActionCtaTypeEnum.Url && action.path) {
    const finalPath = getActionPath(type, action.path);
    return (
      <Link to={finalPath}>
        <Button
          // EndIcon={ArrowForward}
          appearance="secondary"
          className="bg-white hover:bg-white"
          shape="square"
          size="small"
          variant="outlined">
          {action.label}
        </Button>
      </Link>
    );
  }

  if (action.type === RecommendedActionCtaTypeEnum.Url && action.prefill) {
    const finalPath = getActionPath(type, action.prefill);
    return (
      <Link to={finalPath}>
        <Button
          // EndIcon={ArrowForward}
          appearance="secondary"
          className="bg-white hover:bg-white"
          shape="square"
          size="small"
          variant="outlined">
          {action.label}
        </Button>
      </Link>
    );
  }

  if (action.type === RecommendedActionCtaTypeEnum.Button) {
    // modal openers
    return (
      <>
        <Button
          // EndIcon={ArrowForward}
          appearance="secondary"
          className="bg-white hover:bg-white"
          shape="square"
          size="small"
          variant="outlined"
          onClick={openModal}>
          {action.label}
        </Button>

        <Suspense fallback={null}>
          {modalUiState?.type === RecommendedActionTypeEnum.CheckinOkr ? null : null}
          {/* TODO: @aryan02420 move modals to table level */}
          {/* TODO: @aryan02420 add suspense to individual forms */}

          {modalUiState?.type === RecommendedActionTypeEnum.GiveFeedback ? (
            <CreateFeedbackModal
              initialFormValues={modalUiState.payload}
              onClose={closeModal}
              onSuccess={closeModal}
            />
          ) : null}

          {modalUiState?.type === RecommendedActionTypeEnum.AssignContent ? (
            <AssignContentModal initialValues={modalUiState.payload} onClose={closeModal} />
          ) : null}

          {modalUiState?.type === RecommendedActionTypeEnum.GivePraise ? (
            <GivePraiseModal
              initialFormValues={modalUiState.payload}
              onClose={closeModal}
              onSuccess={closeModal}
            />
          ) : null}

          {modalUiState?.type === RecommendedActionTypeEnum.CreateMeeting ? (
            <SetupMeetingModal
              initialValues={modalUiState.payload}
              mode={'create'}
              onClose={closeModal}
              onSuccess={closeModal}
            />
          ) : null}

          {modalUiState?.type === RecommendedActionTypeEnum.SetupOneOnOne ? (
            <Setup1on1Modal
              createMeetingPayload={{ ...modalUiState.payload, type: MeetingTypeEnum.OneOnOne }}
              onClose={closeModal}
              onSuccess={closeModal}
            />
          ) : null}

          {modalUiState?.type === RecommendedActionTypeEnum.CreateHabit ? (
            <HabitCreateModal onClose={closeModal} onSuccess={closeModal} />
          ) : null}

          {modalUiState?.type === RecommendedActionTypeEnum.CreateTask ? (
            <AddOrEditTaskDialogContainer
              mode="create"
              task={{ title: '', ...modalUiState.payload }}
              onClose={closeModal}
              onCreateActionItem={async (data) => closeModal()}
            />
          ) : null}

          {modalUiState?.type === RecommendedActionTypeEnum.RequestFeedback ? (
            <RequestFeedbackModal
              selectedValues={modalUiState.payload as any} // FIXME: check if this is being prefilled correctly
              onClose={closeModal}
            />
          ) : null}

          <CoachRightDrawer
            isOpen={modalUiState?.type === RecommendedActionTypeEnum.CreateKnowledgeDocument}
            startInTeachMode={true}
            onClose={closeModal}
          />
        </Suspense>
      </>
    );
  }

  return null;
}

export default React.memo(InsightRowAction);
